/**
 * TerminalCollectPayment
 * Standalone customer payment collection screen for the terminal mode menu.
 *
 * Flow:
 *   1. PIN gate — Manager PIN or Admin PIN / TOTP
 *   2. Customer search (type name — only customers with open balance shown)
 *   3. Payment form:
 *      - Single "Amount to collect" input
 *      - Auto-allocates FIFO: Penalty → Interest → Oldest open invoice
 *      - Interest accrual banner if charges-preview detects unpaid interest
 *      - Confirmation screen with allocation breakdown
 *   4. Done screen — Print Proof of Payment (58mm thermal only) then Collect Another / Done
 *
 * All credit/AR invoices collected here → always offer PoP print.
 * No full-page print (terminal = 58mm only).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Search, Loader2, ChevronRight, CheckCircle2,
  Wallet, AlertTriangle, ArrowLeft, Printer, SkipForward,
  TrendingUp, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import PrintBridge from '../../lib/PrintBridge';

const fmtPHP = v => `₱${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = iso => { try { return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' }); } catch { return iso?.slice(0, 10) || '—'; } };
const round2 = v => Math.round((parseFloat(v) || 0) * 100) / 100;

// Invoice type priority for FIFO allocation
const typePriority = (inv) => {
  if (inv.sale_type === 'penalty_charge') return 0;
  if (inv.sale_type === 'interest_charge') return 1;
  return 2; // regular invoices, then oldest by date
};

// Sort invoices: Penalty → Interest → Oldest open balance (by date ASC)
const sortedForFIFO = (invoices) =>
  [...invoices].sort((a, b) => {
    const pa = typePriority(a), pb = typePriority(b);
    if (pa !== pb) return pa - pb;
    return (a.date || '').localeCompare(b.date || '');
  });

// Auto-allocate a total amount across sorted invoices
const autoAllocate = (totalAmt, invoices) => {
  const amt = round2(totalAmt);
  let remaining = amt;
  const result = {};
  for (const inv of sortedForFIFO(invoices)) {
    if (remaining <= 0) break;
    const apply = Math.min(remaining, round2(inv.balance));
    if (apply > 0) {
      result[inv.invoice_id] = apply;
      remaining = round2(remaining - apply);
    }
  }
  return result;
};

// ── Step 1: PIN Gate ───────────────────────────────────────────────────────────
function PinGate({ api, branchId, onVerified }) {
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  const verify = async () => {
    if (!pin || pin.length < 4) { setError('Enter a valid PIN or 6-digit TOTP code'); return; }
    setVerifying(true); setError('');
    try {
      const res = await api.post('/verify/verify-pin-action', {
        pin,
        action: 'receive_customer_payment',
        branch_id: branchId,
      });
      if (res.data?.verified || res.data?.verifier_id) {
        onVerified({ verifier_name: res.data.verifier_name || 'Manager', pin });
      } else {
        setError('PIN not accepted — try manager or admin PIN');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid PIN');
    } finally {
      setVerifying(false);
      setPin('');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12 space-y-5">
      <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
        <Wallet size={26} className="text-emerald-700" />
      </div>
      <div className="text-center">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collect Payment</h2>
        <p className="text-xs text-slate-500 mt-1">Enter Manager PIN or Admin PIN to proceed</p>
      </div>
      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        maxLength={6}
        value={pin}
        onChange={e => { setPin(e.target.value); setError(''); }}
        onKeyDown={e => e.key === 'Enter' && verify()}
        placeholder="PIN / TOTP"
        className="h-12 w-full max-w-[200px] text-center font-mono text-xl tracking-widest rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid="collect-payment-pin-input"
        autoComplete="one-time-code"
      />
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1.5">
          <AlertTriangle size={12} /> {error}
        </p>
      )}
      <button
        onClick={verify}
        disabled={verifying || pin.length < 4}
        className="w-full max-w-[200px] py-3 rounded-xl bg-emerald-700 text-white font-semibold text-sm disabled:opacity-50 active:scale-95 transition-all flex items-center justify-center gap-2"
        data-testid="collect-payment-pin-confirm"
      >
        {verifying ? <Loader2 size={14} className="animate-spin" /> : null}
        {verifying ? 'Verifying…' : 'Continue'}
      </button>
    </div>
  );
}

// ── Step 2: Customer Search ────────────────────────────────────────────────────
function CustomerSearch({ api, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const search = useCallback(async (query) => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/customers', { params: { search: query, limit: 15 } });
      const items = res.data.customers || res.data || [];
      setResults(items.filter(c => parseFloat(c.balance || 0) > 0));
    } catch { setResults([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 220);
    return () => clearTimeout(timer.current);
  }, [q, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
          <input
            autoFocus
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search customer name…"
            className="w-full h-10 pl-9 pr-9 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="collect-customer-search"
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">Showing customers with open balance</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {!q && !results.length && (
          <div className="text-center py-10 text-slate-400 text-sm">Start typing to search</div>
        )}
        {q && !loading && !results.length && (
          <div className="text-center py-10 text-slate-400 text-sm">No customers with open balance found</div>
        )}
        {results.map(c => (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-50 hover:bg-emerald-50 transition-colors text-left"
            data-testid={`collect-customer-${c.id}`}
          >
            <div className="min-w-0">
              <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
              <p className="text-[11px] text-slate-400">{c.phone || 'No phone'}</p>
            </div>
            <div className="text-right shrink-0 ml-3">
              <p className="text-sm font-bold text-red-700">{fmtPHP(c.balance)}</p>
              <p className="text-[10px] text-slate-400">open balance</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 3: Payment Form — Single amount + FIFO + interest banner ──────────────
function PaymentForm({ api, customer, branchId, verifierName, verifierPin, onSuccess, onBack }) {
  const [invoices, setInvoices] = useState([]);
  const [amtInput, setAmtInput] = useState('');
  const [allocation, setAllocation] = useState({});
  const [method, setMethod] = useState('Cash');
  const [reference, setReference] = useState('');
  // Method-specific capture (parity with the web Receive Payments form).
  // For Check the single Check No. entry doubles as the reference.
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [bankName, setBankName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [loading, setLoading] = useState(true);
  const [generatingInterest, setGeneratingInterest] = useState(false);
  const [interestPreview, setInterestPreview] = useState(null); // {total_interest, rate}
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'confirm' | 'late_encode'
  const [lateEncodeReason, setLateEncodeReason] = useState('');
  const [error, setError] = useState('');

  const METHODS = ['Cash', 'GCash', 'Maya', 'Bank Transfer', 'Check'];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [acctRes, previewRes] = await Promise.all([
        api.get(`/customers/${customer.id}/account-summary`, { params: { period: 'all' } }),
        api.get(`/customers/${customer.id}/charges-preview`).catch(() => null),
      ]);
      const open = acctRes.data.open_invoices || [];
      setInvoices(open);
      if (previewRes?.data?.total_interest > 0) {
        setInterestPreview({
          total_interest: previewRes.data.total_interest,
          rate: previewRes.data.interest_rate || customer.interest_rate || 0,
        });
      }
    } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  }, [api, customer.id, customer.interest_rate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAmtChange = (val) => {
    setAmtInput(val);
    const n = parseFloat(val) || 0;
    setAllocation(n > 0 ? autoAllocate(n, invoices) : {});
  };

  const handlePayAll = () => {
    const total = invoices.reduce((s, inv) => s + round2(inv.balance), 0);
    setAmtInput(total.toFixed(2));
    setAllocation(autoAllocate(total, invoices));
  };

  const totalCollecting = Object.values(allocation).reduce((s, v) => s + v, 0);
  const totalRemaining = invoices.reduce((s, inv) => {
    const applied = allocation[inv.invoice_id] || 0;
    return s + Math.max(0, round2(inv.balance) - applied);
  }, 0);
  const hasAllocation = totalCollecting > 0.005;

  const handleGenerateInterest = async () => {
    setGeneratingInterest(true);
    try {
      const res = await api.post(`/customers/${customer.id}/generate-interest`, {
        branch_id: branchId,
      });
      if (res.data.total_interest > 0) {
        toast.success(`Interest invoice created — ${fmtPHP(res.data.total_interest)}`);
        setInterestPreview(null);
        await loadData();
      } else {
        toast.info(res.data.message || 'No interest to generate');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate interest invoice');
    }
    setGeneratingInterest(false);
  };

  const buildAllocations = () =>
    Object.entries(allocation)
      .filter(([, amt]) => amt > 0)
      .map(([invoice_id, amount]) => ({ invoice_id, amount: round2(amount) }));

  const submitPayment = async (lateEncode = null) => {
    setSubmitting(true); setError('');
    try {
      const payload = {
        allocations: buildAllocations(),
        method,
        // Check: the check number IS the reference (owner 2026-08-10) — one
        // entry feeds the Z-Report side AND Check Monitoring.
        reference: method === 'Check' ? checkNumber.trim() : reference,
        branch_id: branchId,
        idempotency_key: `terminal-collect-${Date.now()}`,
      };
      if (lateEncode) payload.late_encode = lateEncode;
      if (method === 'Check') {
        payload.check_number = checkNumber.trim();
        if (checkDate) payload.check_date = checkDate;
        payload.bank_name = bankName.trim();
      }
      if (method === 'Bank Transfer' && bankName.trim()) payload.bank_name = bankName.trim();
      if ((method === 'GCash' || method === 'Maya') && senderName.trim()) payload.sender = senderName.trim();

      const res = await api.post(`/customers/${customer.id}/receive-payment`, payload);

      onSuccess({
        customer,
        totalCollecting: round2(totalCollecting),
        totalRemaining: round2(totalRemaining),
        method,
        proofOfPayment: res.data.proof_of_payment || null,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Payment failed';
      const isClosedDay = typeof msg === 'string' && /already closed|late.*encod/i.test(msg);
      if (isClosedDay && verifierPin) {
        // Closed-day detected → ask for a reason then retry
        setError('');
        setStep('late_encode');
      } else {
        setError(typeof msg === 'string' ? msg : 'Payment failed');
        setStep('confirm');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => submitPayment(null);

  const handleLateEncodeSubmit = () => {
    if (!lateEncodeReason.trim()) { setError('Please enter a reason for the late payment'); return; }
    submitPayment({ pin: verifierPin, reason: lateEncodeReason.trim() });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  // ── Late encode step ──────────────────────────────────────────────────────
  if (step === 'late_encode') {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 pt-4 pb-3 border-b">
          <button onClick={() => setStep('confirm')} className="flex items-center gap-1.5 text-slate-500 text-xs mb-2">
            <ArrowLeft size={13} /> Back
          </button>
          <h3 className="font-bold text-base text-slate-900">Day Already Closed</h3>
        </div>
        <div className="flex-1 px-4 py-4 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-amber-800">Late Payment Entry</p>
            <p className="text-xs text-amber-700 mt-1">
              Today's books are closed. Collecting {fmtPHP(totalCollecting)} from <strong>{customer.name}</strong> requires a reason for late recording.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Reason</label>
            <input
              type="text"
              autoFocus
              value={lateEncodeReason}
              onChange={e => { setLateEncodeReason(e.target.value); setError(''); }}
              placeholder="e.g. Customer paid after closing"
              className="w-full h-10 px-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="late-encode-reason-input"
            />
          </div>
          {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} />{error}</p>}
        </div>
        <div className="px-4 pb-6 pt-2 border-t">
          <button
            onClick={handleLateEncodeSubmit}
            disabled={submitting || !lateEncodeReason.trim()}
            className="w-full py-4 rounded-2xl bg-emerald-700 text-white font-bold text-base disabled:opacity-50 active:scale-95 transition-all flex items-center justify-center gap-2"
            data-testid="late-encode-submit-btn"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {submitting ? 'Processing…' : `Record Late Payment — ${fmtPHP(totalCollecting)}`}
          </button>
        </div>
      </div>
    );
  }

  // ── Confirm step ──────────────────────────────────────────────────────────
  if (step === 'confirm') {
    const sortedAlloc = sortedForFIFO(invoices).filter(inv => (allocation[inv.invoice_id] || 0) > 0);
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 pt-4 pb-3 border-b">
          <button onClick={() => setStep('form')} className="flex items-center gap-1.5 text-slate-500 text-xs mb-2">
            <ArrowLeft size={13} /> Edit amount
          </button>
          <h3 className="font-bold text-base text-slate-900">Confirm Payment</h3>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Customer</span>
              <span className="font-semibold">{customer.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Collecting</span>
              <span className="font-bold text-emerald-800 text-base">{fmtPHP(totalCollecting)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Remaining after</span>
              <span className={`font-bold text-sm ${totalRemaining > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {totalRemaining > 0 ? fmtPHP(totalRemaining) : 'FULLY SETTLED'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Method</span>
              <span className="font-semibold">{method}</span>
            </div>
            {reference && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Reference</span>
                <span className="font-semibold">{reference}</span>
              </div>
            )}
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Authorized by</span>
              <span className="text-slate-700">{verifierName}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Allocation (FIFO order)</p>
            {sortedAlloc.map(inv => (
              <div key={inv.invoice_id} className="flex justify-between text-xs bg-white border border-slate-200 rounded-lg px-3 py-2">
                <div>
                  <span className="font-semibold text-slate-800">{inv.invoice_number}</span>
                  {(inv.sale_type === 'interest_charge' || inv.sale_type === 'penalty_charge') && (
                    <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${inv.sale_type === 'penalty_charge' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {inv.sale_type === 'penalty_charge' ? 'Penalty' : 'Interest'}
                    </span>
                  )}
                  {inv.due_date && <p className="text-[10px] text-slate-400 mt-0.5">Due {fmtDate(inv.due_date)}</p>}
                </div>
                <span className="font-bold text-emerald-700">{fmtPHP(allocation[inv.invoice_id])}</span>
              </div>
            ))}
          </div>

          {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={12} />{error}</p>}
        </div>
        <div className="px-4 pb-6 pt-2 border-t space-y-2">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-4 rounded-2xl bg-emerald-700 text-white font-bold text-base disabled:opacity-50 active:scale-95 transition-all flex items-center justify-center gap-2"
            data-testid="collect-payment-confirm-btn"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {submitting ? 'Processing…' : `Confirm — ${fmtPHP(totalCollecting)}`}
          </button>
          <button onClick={() => setStep('form')} className="w-full py-2.5 text-sm text-slate-400">
            Edit amount
          </button>
        </div>
      </div>
    );
  }

  // ── Form step ─────────────────────────────────────────────────────────────
  const totalOpen = invoices.reduce((s, inv) => s + round2(inv.balance), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b">
        <button onClick={onBack} className="flex items-center gap-1.5 text-slate-500 text-xs mb-2">
          <ArrowLeft size={13} /> Different customer
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-base text-slate-900">{customer.name}</h3>
            <p className="text-xs text-slate-500">{customer.phone || ''}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Total open</p>
            <p className="text-base font-bold text-red-700">{fmtPHP(totalOpen)}</p>
          </div>
        </div>

        {/* Method selector */}
        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-0.5">
          {METHODS.map(m => (
            <button key={m} onClick={() => setMethod(m)}
              className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 transition-colors ${method === m ? 'bg-[#1A4D2E] text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`collect-method-${m.toLowerCase().replace(/ /g, '-')}`}>
              {m}
            </button>
          ))}
        </div>
        {method !== 'Cash' && method !== 'Check' && (
          <input
            type="text"
            value={reference}
            onChange={e => setReference(e.target.value)}
            placeholder={`${method} ref / transaction no.`}
            className="mt-2 w-full h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
            data-testid="collect-reference-input"
          />
        )}
        {method === 'Check' && (
          <div className="mt-2 space-y-1.5">
            <div className="flex gap-1.5">
              <input
                type="text"
                value={checkNumber}
                onChange={e => setCheckNumber(e.target.value)}
                placeholder="Check no."
                className="flex-1 h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
                data-testid="collect-check-number-input"
              />
              <input
                type="text"
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                placeholder="Bank"
                className="w-24 h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
                data-testid="collect-bank-name-input"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-400 uppercase shrink-0">Check date</span>
              <input
                type="date"
                value={checkDate}
                onChange={e => setCheckDate(e.target.value)}
                title="Date printed on the check. For post-dated checks, enter the future date."
                className="flex-1 h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
                data-testid="collect-check-date-input"
              />
            </div>
          </div>
        )}
        {(method === 'GCash' || method === 'Maya') && (
          <input
            type="text"
            value={senderName}
            onChange={e => setSenderName(e.target.value)}
            placeholder="Sender name / mobile no. (optional)"
            className="mt-1.5 w-full h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
            data-testid="collect-sender-input"
          />
        )}
        {method === 'Bank Transfer' && (
          <input
            type="text"
            value={bankName}
            onChange={e => setBankName(e.target.value)}
            placeholder="Bank (optional)"
            className="mt-1.5 w-full h-8 px-3 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none"
            data-testid="collect-bank-name-input"
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Interest accrual banner */}
        {interestPreview && interestPreview.total_interest > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="interest-banner">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-amber-600 shrink-0" />
              <p className="text-xs font-semibold text-amber-800">
                Accrued interest: {fmtPHP(interestPreview.total_interest)}
              </p>
            </div>
            <p className="text-[11px] text-amber-700">
              Interest not yet invoiced. Add it to the balance before collecting.
            </p>
            <button
              onClick={handleGenerateInterest}
              disabled={generatingInterest}
              className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 border border-amber-300 rounded-lg px-3 py-1.5 bg-amber-100 hover:bg-amber-200 active:scale-95 transition-all disabled:opacity-50"
              data-testid="generate-interest-btn"
            >
              {generatingInterest ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              {generatingInterest ? 'Generating…' : 'Generate Interest Invoice'}
            </button>
          </div>
        )}

        {/* Single amount input */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount to Collect</p>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-lg font-mono">₱</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              max={totalOpen}
              step="0.01"
              value={amtInput}
              onChange={e => handleAmtChange(e.target.value)}
              onFocus={e => e.target.select()}
              placeholder="0.00"
              className="flex-1 h-12 text-right font-mono text-xl rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="collect-amount-input"
              autoFocus
            />
            <button
              onClick={handlePayAll}
              className="text-xs text-emerald-700 border border-emerald-200 rounded-lg px-3 py-2.5 hover:bg-emerald-50 shrink-0 font-semibold"
              data-testid="collect-pay-all-btn"
            >
              Pay All
            </button>
          </div>
          {totalCollecting > 0 && (
            <div className="flex justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
              <span>Remaining after payment</span>
              <span className={`font-bold ${totalRemaining > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {totalRemaining > 0 ? fmtPHP(totalRemaining) : 'FULLY SETTLED'}
              </span>
            </div>
          )}
        </div>

        {/* FIFO allocation preview */}
        {hasAllocation && (
          <div className="space-y-1.5" data-testid="allocation-preview">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Auto-allocation (Penalty → Interest → Oldest)</p>
            {sortedForFIFO(invoices)
              .filter(inv => (allocation[inv.invoice_id] || 0) > 0)
              .map(inv => (
                <div key={inv.invoice_id} className="flex justify-between items-center text-xs bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-slate-800">{inv.invoice_number}</span>
                      {(inv.sale_type === 'interest_charge' || inv.sale_type === 'penalty_charge') && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${inv.sale_type === 'penalty_charge' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {inv.sale_type === 'penalty_charge' ? 'Penalty' : 'Interest'}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Bal: {fmtPHP(inv.balance)}
                      {inv.days_overdue > 0 && <span className="text-red-500 ml-1">+{inv.days_overdue}d overdue</span>}
                    </p>
                  </div>
                  <span className="font-bold text-emerald-700 shrink-0 ml-2">{fmtPHP(allocation[inv.invoice_id])}</span>
                </div>
              ))}
          </div>
        )}

        {/* Unaffected invoices (balance unchanged) */}
        {hasAllocation && sortedForFIFO(invoices).some(inv => !(allocation[inv.invoice_id] || 0)) && (
          <div className="space-y-1">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Remaining open (not covered)</p>
            {sortedForFIFO(invoices)
              .filter(inv => !(allocation[inv.invoice_id] || 0))
              .map(inv => (
                <div key={inv.invoice_id} className="flex justify-between text-[11px] text-slate-400 px-3 py-1.5 rounded-lg bg-slate-50">
                  <span>{inv.invoice_number}</span>
                  <span>{fmtPHP(inv.balance)}</span>
                </div>
              ))}
          </div>
        )}

        {!invoices.length && (
          <div className="text-center py-10 text-slate-400 text-sm">No open invoices</div>
        )}
      </div>

      <div className="px-4 pb-6 pt-2 border-t">
        <button
          onClick={() => {
            if (!hasAllocation) { setError('Enter an amount to collect'); return; }
            setError('');
            setStep('confirm');
          }}
          disabled={!hasAllocation}
          className="w-full py-4 rounded-2xl bg-emerald-700 text-white font-bold text-base disabled:opacity-40 active:scale-95 transition-all"
          data-testid="collect-review-btn"
        >
          Review — {fmtPHP(totalCollecting)}
        </button>
        {error && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle size={12} />{error}</p>}
      </div>
    </div>
  );
}

// ── Step 4: Done — PoP print prompt then Collect Another / Done ────────────────
function DoneScreen({ result, businessInfo, onReset, onClose }) {
  const { customer, totalCollecting, totalRemaining, method, proofOfPayment } = result;
  const [printed, setPrinted] = useState(false);

  const handlePrint = async () => {
    if (!proofOfPayment) return;
    try {
      await PrintBridge.print({
        type: 'proof_of_payment_terminal',
        data: proofOfPayment,
        format: 'thermal',
        businessInfo: businessInfo || {},
      });
    } catch (err) {
      toast.error('Print failed — check printer connection');
      return;
    }
    setPrinted(true);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 text-center space-y-5">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 size={32} className="text-emerald-600" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">Payment Recorded!</h3>
        <p className="text-sm text-slate-600 mt-1">
          {fmtPHP(totalCollecting)} collected from <strong>{customer.name}</strong>
        </p>
        <p className="text-xs text-slate-400 mt-0.5">{method}</p>
        {totalRemaining > 0 ? (
          <p className="text-xs text-red-600 mt-1">Remaining balance: {fmtPHP(totalRemaining)}</p>
        ) : (
          <p className="text-xs text-emerald-600 font-semibold mt-1">Account fully settled</p>
        )}
      </div>

      {/* PoP Print prompt — only shown when backend returned a PoP */}
      {proofOfPayment && !printed && (
        <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="pop-print-prompt">
          <div className="flex items-center gap-2 justify-center">
            <Printer size={15} className="text-slate-600" />
            <p className="text-sm font-semibold text-slate-800">Print Proof of Payment?</p>
          </div>
          <p className="text-xs text-slate-500">
            Transaction No: <span className="font-mono font-bold">{proofOfPayment.receipt_number}</span>
          </p>
          <button
            onClick={handlePrint}
            className="w-full py-3 rounded-xl bg-[#1A4D2E] text-white font-semibold text-sm active:scale-95 transition-all flex items-center justify-center gap-2"
            data-testid="pop-print-btn"
          >
            <Printer size={14} /> Print 58mm
          </button>
          <button
            onClick={() => setPrinted(true)}
            className="w-full py-2 text-xs text-slate-400 flex items-center justify-center gap-1"
            data-testid="pop-skip-btn"
          >
            <SkipForward size={11} /> Skip
          </button>
        </div>
      )}

      {/* After print or skip — show Collect Another / Done */}
      {(!proofOfPayment || printed) && (
        <div className="flex gap-3 w-full">
          <button
            onClick={onReset}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 active:scale-95 transition-all"
            data-testid="collect-another-btn"
          >
            Collect Another
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-emerald-700 text-white text-sm font-semibold active:scale-95 transition-all"
            data-testid="collect-payment-done-btn"
          >
            Done
          </button>
        </div>
      )}

      {/* After printing — still show Done */}
      {printed && proofOfPayment && (
        <p className="text-xs text-emerald-600 flex items-center gap-1">
          <CheckCircle2 size={11} /> Receipt printed
        </p>
      )}
    </div>
  );
}

// ── Root Component ─────────────────────────────────────────────────────────────
export default function TerminalCollectPayment({ api, session, onClose, businessInfo, prefillCustomer }) {
  const [step, setStep] = useState(prefillCustomer ? 'payment' : 'pin');
  const [verifier, setVerifier] = useState(null);
  const [customer, setCustomer] = useState(prefillCustomer || null);
  const [result, setResult] = useState(null);

  const handleVerified = (v) => { setVerifier(v); setStep('search'); };
  const handleCustomerSelect = (c) => { setCustomer(c); setStep('payment'); };
  const handleSuccess = (r) => { setResult(r); setStep('done'); };
  const handleReset = () => {
    setResult(null);
    setCustomer(prefillCustomer || null);
    setStep(prefillCustomer ? 'payment' : 'search');
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="collect-payment-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
        <div className="flex items-center gap-2">
          <Wallet size={16} className="text-emerald-700" />
          <h2 className="font-bold text-sm text-slate-900" style={{ fontFamily: 'Manrope' }}>Collect Payment</h2>
          {verifier && step !== 'pin' && (
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{verifier.verifier_name}</span>
          )}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="collect-payment-close">
          <X size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Step indicators */}
      {step !== 'done' && (
        <div className="bg-white border-b px-4 py-2 flex items-center gap-3 shrink-0">
          {['pin', 'search', 'payment'].map((s, i) => (
            <div key={s} className={`flex items-center gap-1.5 text-[11px] font-medium ${
              step === s ? 'text-emerald-700' : (
                ['pin', 'search', 'payment'].indexOf(step) > i ? 'text-emerald-500' : 'text-slate-400'
              )
            }`}>
              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                step === s ? 'bg-emerald-700 text-white' : (
                  ['pin', 'search', 'payment'].indexOf(step) > i ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-200 text-slate-500'
                )
              }`}>{i + 1}</div>
              {['PIN', 'Customer', 'Payment'][i]}
              {i < 2 && <ChevronRight size={10} className="text-slate-300" />}
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {step === 'pin' && (
          <PinGate api={api} branchId={session.branchId} onVerified={handleVerified} />
        )}
        {step === 'search' && (
          <CustomerSearch api={api} onSelect={handleCustomerSelect} />
        )}
        {step === 'payment' && customer && (
          <PaymentForm
            api={api}
            customer={customer}
            branchId={session.branchId}
            verifierName={verifier?.verifier_name || 'Manager'}
            verifierPin={verifier?.pin || ''}
            onSuccess={handleSuccess}
            onBack={() => { setCustomer(null); setStep('search'); }}
          />
        )}
        {step === 'done' && result && (
          <DoneScreen
            result={result}
            businessInfo={businessInfo}
            onReset={handleReset}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
